import api from './api';
import portalAPI from './portalAPI';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

// ─── Public (no auth) ───────────────────────────────────────

const publicFetch = async (endpoint: string, options: RequestInit = {}) => {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json();
};

export const publicAgreementAPI = {
  getDemoNDA: () => publicFetch('/agreements/demo-nda/'),
  signDemoNDA: (data: {
    name: string;
    email: string;
    company?: string;
    address?: string;
    signature: string;
  }) => publicFetch('/agreements/demo-nda/sign/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  checkDemoNDA: (email: string) =>
    publicFetch(`/agreements/demo-nda/check/?email=${encodeURIComponent(email)}`),
};

// ─── Portal (authenticated via portalAPI) ────────────────────

export const portalAgreementAPI = {
  getPlatformAgreement: () => portalAPI.get('/agreements/platform/').then(r => r.data),
  acceptPlatformAgreement: () => portalAPI.post('/agreements/platform/accept/').then(r => r.data),
  getPlatformStatus: () => portalAPI.get('/agreements/platform/status/').then(r => r.data),
  getEggWatchAgreement: () => portalAPI.get('/agreements/egg-watch/').then(r => r.data),
  acceptEggWatchAgreement: (signature: string) =>
    portalAPI.post('/agreements/egg-watch/accept/', { signature }).then(r => r.data),
};

// ─── Admin (authenticated via api) ──────────────────────────

export const adminAgreementAPI = {
  // Templates
  getTemplates: (type?: string) =>
    api.get('/agreements/admin/templates/', { params: type ? { type } : {} }).then(r => r.data),
  createTemplate: (data: {
    type: string;
    version: string;
    title: string;
    content: string;
    is_active?: boolean;
    requires_witness?: boolean;
  }) => api.post('/agreements/admin/templates/', data).then(r => r.data),

  // Signed
  getSignedList: (params?: { type?: string; status?: string; search?: string }) =>
    api.get('/agreements/admin/signed/', { params }).then(r => r.data),
  getSignedDetail: (id: string) =>
    api.get(`/agreements/admin/signed/${id}/`).then(r => r.data),

  // Signing sessions
  createSigning: (data: {
    template_id: number;
    name?: string;
    email?: string;
    company?: string;
    address?: string;
  }) => api.post('/agreements/admin/create-signing/', data).then(r => r.data),

  submitSignature: (id: string, data: { signature: string; name?: string }) =>
    publicFetch(`/agreements/admin/submit-signature/${id}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  countersign: (id: string, signature?: string) =>
    api.post(`/agreements/admin/countersign/${id}/`, { signature }).then(r => r.data),

  sendEmail: (id: string) =>
    api.post(`/agreements/admin/send-email/${id}/`).then(r => r.data),

  // Dashboard
  getDashboard: () => api.get('/agreements/admin/dashboard/').then(r => r.data),

  // Signature profile
  getSignatureProfile: () =>
    api.get('/agreements/admin/signature-profile/').then(r => r.data),
  updateSignatureProfile: (data: { signature?: string; pin?: string }) =>
    api.post('/agreements/admin/signature-profile/', data).then(r => r.data),
  verifyPin: (pin: string) =>
    api.post('/agreements/admin/verify-pin/', { pin }).then(r => r.data),
};
